import { useEffect, useRef, useState } from 'react'
import { Image as ImageIcon, X, AlertTriangle } from 'lucide-react'
import { mediaManager, type BackgroundEffect } from '../../services/mediaManager'
import { useLivekitStore } from '../../stores/livekitStore'
import { useAuthStore } from '../../stores/authStore'
import { useVoiceStore } from '../../stores/voiceStore'
import { useToastStore } from '../../stores/toastStore'
import { wallpaperApi, authApi } from '../../services/api'
import { mediaUrl } from '../../services/config'

const BLUR_PRESETS: Array<{ key: 'none' | 'blur' | 'strong-blur'; label: string; effect: BackgroundEffect }> = [
  { key: 'none', label: 'None', effect: { kind: 'none' } },
  { key: 'blur', label: 'Blur', effect: { kind: 'blur', strength: 8 } },
  { key: 'strong-blur', label: 'Strong blur', effect: { kind: 'blur', strength: 18 } },
]

interface PersistedSettings {
  backgroundEffect?: { kind: 'none' | 'blur' | 'image'; strength?: number; imageUrl?: string }
  [k: string]: any
}

function settingsToEffect(s: PersistedSettings | null | undefined): BackgroundEffect {
  const e = s?.backgroundEffect
  if (!e || e.kind === 'none') return { kind: 'none' }
  if (e.kind === 'blur') return { kind: 'blur', strength: e.strength ?? 8 }
  if (e.kind === 'image' && e.imageUrl) return { kind: 'image', imageUrl: e.imageUrl }
  return { kind: 'none' }
}

function effectKeyOf(e: BackgroundEffect): string {
  if (e.kind === 'none') return 'none'
  if (e.kind === 'blur') return (e.strength ?? 0) >= 14 ? 'strong-blur' : 'blur'
  return `image:${e.imageUrl}`
}

const lowSpec =
  typeof navigator !== 'undefined' &&
  ((navigator.hardwareConcurrency ?? 8) < 4 || ((navigator as any).deviceMemory ?? 8) < 4)

// OSPROD-134: `compact` shrinks the trigger from a 40px disc to a 32px
// disc when this picker is rendered inside another dropdown menu (the
// Cam dropdown's settings row). Behaviour identical — only the trigger
// sizing changes.
export default function BackgroundEffectPicker({ compact = false }: { compact?: boolean } = {}) {
  const room = useLivekitStore((s) => s.room)
  const user = useAuthStore((s) => s.user)

  const [open, setOpen] = useState(false)
  const [effect, setEffect] = useState<BackgroundEffect>(() => settingsToEffect(user?.settings))
  const [wallpapers, setWallpapers] = useState<Array<{ id: string; url: string; name: string }>>([])
  const ref = useRef<HTMLDivElement>(null)

  // Apply on mount + whenever the user's settings change.
  useEffect(() => {
    const e = settingsToEffect(user?.settings)
    setEffect(e)
    // OSPROD-232: only (re)apply to the live pipeline when the desired effect
    // actually differs from what mediaManager already has. Merely MOUNTING this
    // picker (the camera-settings menu mounts it every time it opens) used to
    // call setBackgroundEffect unconditionally → applyVideoEffectsToRoom rebuilt
    // the processor → the stock MediaPipe graph was destroyed+recreated every
    // time the menu opened → video flickered with "Empty video frame" /
    // "Stream closed" + a VideoFrame leak. A genuine change (settings updated, or
    // the first publish on (re)join) still differs and applies.
    const cur = mediaManager.getBackgroundEffect()
    const same =
      cur.kind === e.kind &&
      (e.kind !== 'blur' || (cur as { strength?: number }).strength === (e as { strength?: number }).strength) &&
      (e.kind !== 'image' || (cur as { imageUrl?: string }).imageUrl === (e as { imageUrl?: string }).imageUrl)
    if (!same) {
      mediaManager.setBackgroundEffect(room, e).catch(() => {})
    }
    // Couple auto-frame with the background on (re)join: a saved background
    // suppresses auto-frame, no background restores it (only if auto-suppressed).
    useVoiceStore.getState().syncBackgroundCoupling(e.kind !== 'none')
    // We deliberately re-run when room changes too — first publish needs the
    // processor attached.
  }, [room, user?.id])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  useEffect(() => {
    if (!open || wallpapers.length) return
    wallpaperApi.list().then((res: any) => {
      const items = (res.data ?? []).slice(0, 16)
      setWallpapers(items)
    }).catch(() => {})
  }, [open, wallpapers.length])

  const apply = async (e: BackgroundEffect) => {
    setEffect(e)
    try {
      await mediaManager.setBackgroundEffect(room, e)
    } catch (err: any) {
      console.warn('apply background effect failed', err)
      // Roll the local pill back to "None" so the user isn't left with
      // a misleading active state, and surface the reason — previously
      // this was a silent console.warn.
      setEffect({ kind: 'none' })
      // The background did NOT apply — keep auto-frame coupling in sync with
      // reality (no background active) so it isn't wrongly suppressed.
      useVoiceStore.getState().syncBackgroundCoupling(false)
      useToastStore.getState().showToast({
        id: `bg-fail-${Date.now()}`,
        type: 'error',
        title: "Couldn't apply background",
        body: err?.message ?? 'Unknown error',
      })
      return
    }
    // Background applied — suppress auto-frame when an effect is on, restore
    // it when set to none (restore only fires if it was auto-suppressed).
    useVoiceStore.getState().syncBackgroundCoupling(e.kind !== 'none')
    // Persist on the user's settings so it sticks across reloads / rooms.
    try {
      const persist: PersistedSettings['backgroundEffect'] =
        e.kind === 'none' ? { kind: 'none' }
        : e.kind === 'blur' ? { kind: 'blur', strength: e.strength ?? 8 }
        : { kind: 'image', imageUrl: e.imageUrl }
      const merged = { ...(user?.settings ?? {}), backgroundEffect: persist }
      const res = await authApi.updateMe({ settings: merged } as any)
      useAuthStore.setState({ user: res.data })
    } catch {}
  }

  const isActive = effect.kind !== 'none'
  const currentKey = effectKeyOf(effect)
  const sz = compact ? 'w-8 h-8' : 'w-10 h-10'
  const iconSize = compact ? 14 : 20

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={isActive ? 'Background effect on — click to change' : 'Background effect'}
        className={`${sz} rounded-full flex items-center justify-center transition duration-150 ${
          isActive ? 'bg-blue-600/80 text-white hover:bg-blue-600' : 'bg-muted text-foreground hover:bg-muted/80'
        }`}
      >
        <ImageIcon size={iconSize} />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 right-0 w-72 bg-card rounded-lg ring-1 ring-border shadow-lg overflow-hidden z-30">
          <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border flex items-center justify-between">
            <span>Background</span>
            {lowSpec && (
              <span className="flex items-center gap-1 text-yellow-500 normal-case font-normal text-[10px]">
                <AlertTriangle size={11} /> Low-spec — may stutter
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 p-2">
            {BLUR_PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => { apply(p.effect) }}
                className={`relative aspect-video rounded-md flex items-center justify-center text-xs font-medium border ${
                  currentKey === p.key
                    ? 'border-primary text-primary bg-primary/10'
                    : 'border-border text-foreground bg-muted hover:bg-muted/80'
                }`}
              >
                {p.key === 'none' && <X size={14} className="absolute top-1 right-1 opacity-60" />}
                {p.label}
              </button>
            ))}
          </div>
          {wallpapers.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-t border-border">
                Virtual background
              </div>
              <div className="grid grid-cols-3 gap-2 p-2 max-h-48 overflow-y-auto">
                {wallpapers.map((w) => {
                  const isSel = currentKey === `image:${w.url}`
                  return (
                    <button
                      key={w.id}
                      onClick={() => apply({ kind: 'image', imageUrl: w.url })}
                      className={`aspect-video rounded-md overflow-hidden bg-muted border ${
                        isSel ? 'border-primary ring-2 ring-primary/40' : 'border-border'
                      }`}
                      title={w.name}
                    >
                      <img src={mediaUrl(w.url)} alt={w.name} className="w-full h-full object-cover" />
                    </button>
                  )
                })}
              </div>
            </>
          )}
          <div className="px-3 py-2 text-[10px] text-muted-foreground border-t border-border">
            Effect runs locally — others see the processed video.
          </div>
        </div>
      )}
    </div>
  )
}
